<template>
  <div>
    <form-item prop="push_title" :label="$t('CampaignForm_Push_Field_Subject')">
      <snippet-input
        id="push_title"
        v-model="value.push_title"
        :optimove="isOptimove"
        :salesforce="isSalesforce"
        data-qa="mobile-push-title-input"
      />
    </form-item>

    <!-- Message Text -->
    <form-item prop="push_text" :label="$t('CampaignForm_Push_Field_Text')">
      <snippet-input
        type="textarea"
        v-model="value.push_text"
        :optimove="isOptimove"
        :salesforce="isSalesforce"
        data-qa="mobile-push-message-input"
      />
    </form-item>

    <!-- Voucher code settings -->
    <template v-if="model.voucher_codes_enabled">
      <form-item
        prop="voucher_code_enabled"
        :label="$t('CampaignForm_VoucherType_Title')"
      >
        <enum-select
          v-model="value.voucher_code_enabled"
          :options="voucherTypesLabels"
          :number="true"
          data-qa="voucher_code_enabled"
        />
      </form-item>

      <form-item
        prop="voucher_code_position"
        :label="$t('CampaignForm_VoucherPosition_Title')"
        v-if="value.voucher_code_enabled === 1"
      >
        <enum-select
          v-model="value.voucher_code_position"
          :options="voucherPositionsLabels"
          :number="true"
          data-qa="voucher_code_position"
        />
      </form-item>
    </template>

    <form-item
      class="push-icon-ct"
      prop="push_picture"
      :label="$t('CampaignForm_UploadType_Upload')"
    >
      <campaign-image-upload
        :value="value.push_picture"
        :file.sync="value.push_picture"
        :message-type="channelTypes[osType + '_push']"
      />
    </form-item>

    <!-- Push Category -->
    <form-item
      prop="push_category_id"
      :label="$t('CampaignForm_Push_Category')"
    >
      <el-autocomplete
        :placeholder="$t('CampaignForm_Select')"
        clearable
        v-model="category"
        :fetch-suggestions="querySearchCategory"
        @select="handleSelectCategory"
        @clear="clearCategorySelection"
        data-qa="category-dropdown"
      />
    </form-item>

    <!-- Default Action -->
    <form-item :label="$t('CampaignForm_Push_DefaultAction')">
      <el-select
        v-model="defaultAction"
        @change="updateDefaultAction"
        :disabled="isCategorySelected"
        data-qa="mobile-default-action-dropdown"
      >
        <el-option
          value=""
          :label="$t('CampaignForm_Push_DefaultAction_None')"
          data-qa="mobile-action-none"
        />
        <el-option
          value="url"
          :label="$t('CampaignForm_Push_DefaultAction_Url')"
          data-qa="mobile-action-open-url"
        />
        <el-option
          value="deeplink"
          :label="$t('CampaignForm_Push_DefaultAction_Deeplink')"
          data-qa="mobile-action-go-to-deeplink"
        />
        <el-option
          v-if="hasInApp"
          value="inapp"
          :label="$t('CampaignForm_Push_DefaultAction_InAppMessage')"
          data-qa="mobile-action-in-app-message"
        />
      </el-select>
    </form-item>
    <form-item
      v-if="defaultAction === 'url'"
      :label="$t('CampaignForm_Push_DefaultAction_Url')"
    >
      <el-input
        v-model="value.push_action.url"
        data-qa="mobile-push-url-input"
      />
    </form-item>
    <form-item
      v-if="defaultAction === 'deeplink'"
      :label="$t('CampaignForm_Push_DefaultAction_Deeplink')"
    >
      <el-input
        v-model="value.push_action.deeplink"
        data-qa="mobile-push-deeplink-input"
      />
    </form-item>

    <template v-for="(button, index) in buttons">
      <form-item
        v-if="button.action !== ''"
        :key="index"
        :label="
          $t('CampaignForm_Push_ActionType', {
            action:
              button.action === 'url'
                ? button.action.toUpperCase()
                : button.action.charAt(0).toUpperCase() +
                  button.action.slice(1),
            title: button.title
          })
        "
      >
        <el-input v-model="value.push_buttons[button.id][button.action]" />
      </form-item>
    </template>

    <!-- iOS Interruption Level --->
    <form-item
      v-if="osType === 'ios'"
      prop="push_interruption_level"
      :label="$t('CampaignForm_Push_PushInterruption_Title')"
    >
      <el-select v-model="value.push_interruption_level">
        <el-option
          v-for="interruption in iosNotificationInterruptionLevelEnum"
          :key="interruption"
          :label="$t(iosNotificationInterruptionLevelLabels[interruption])"
          :value="interruption"
        />
      </el-select>
    </form-item>

    <!-- Payload -->
    <form-item prop="payload_add" :label="$t('CampaignForm_Push_Payload')">
      <template v-for="payload in pushCategoryPayload">
        <div class="rowCt">
          <form-item :prop="payload.key">
            <el-input :disabled="true" v-model="payload.key" />
          </form-item>
          <form-item :prop="payload.value">
            <el-input :disabled="true" v-model="payload.value" />
          </form-item>
          <icon class="icon" glyph="bin" style="visibility: hidden" />
        </div>
      </template>
      <template v-for="(payload, index) in value.payload_add">
        <div class="rowCt">
          <form-item :prop="value.payload_add[index].key">
            <el-input
              :disabled="value.payload_add[index].fromCategory"
              v-model="value.payload_add[index].key"
              :placeholder="$t('CampaignForm_Push_PayloadKey')"
            />
          </form-item>
          <form-item :prop="value.payload_add[index].value">
            <el-input
              v-model="value.payload_add[index].value"
              :placeholder="$t('CampaignForm_Push_PayloadValue')"
            />
          </form-item>
          <icon
            v-if="!value.payload_add[index].fromCategory"
            class="icon"
            glyph="bin"
            @click="removePayload(index)"
          />
        </div>
      </template>
      <div>
        <el-button
          type="primary"
          plain
          @click="addPayload"
          :disabled="isPayloadBtnDisabled()"
        >
          {{ $t('CampaignForm_Push_AddPayload') }}
        </el-button>
      </div>
    </form-item>
  </div>
</template>

<script>
import FormItem from '@/common/components/form/FormItem'
import { channelTypes } from '@/modules/campaign-manage/enum/Labels'
import { EventBus } from '@/core/EventBus'
import RestApi from '@/core/services/RestApi'
import { projectEndpoint } from '@/core/helpers'
import SnippetInput from '@/modules/campaign-manage/components/SnippetInput'
import { campaignTriggerTypes } from '@/modules/campaign-manage/enum/CampaignTriggerTypesEnum'
import { voucherTypesLabels } from '@/modules/campaign-manage/enum/VoucherTypesEnum'
import { voucherPositionsLabels } from '@/modules/campaign-manage/enum/VoucherPositionsEnum'
import CampaignImageUpload from '@/modules/campaign-manage/components/content/CampaignImageUpload'
import {
  iosNotificationInterruptionLevelEnum,
  iosNotificationInterruptionLevelLabels
} from '@/modules/campaign-manage/enum/IosNotificationInterruptionLevelEnum'
import EnumSelect from '@/common/components/form/EnumSelect'

export default {
  name: 'MobilePushForm',
  components: {
    CampaignImageUpload,
    SnippetInput,
    FormItem,
    EnumSelect
  },
  props: {
    value: {
      required: true
    },
    osType: {
      required: true
    },
    model: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      channelTypes,
      voucherTypesLabels,
      voucherPositionsLabels,
      iosNotificationInterruptionLevelEnum,
      iosNotificationInterruptionLevelLabels,
      category: '',
      defaultAction: '',
      isCategorySelected: false,
      actions: [],
      buttons: [],
      pushCategoryPayload: []
    }
  },
  mounted() {
    EventBus.$on('applyPushTemplate-' + this.key, data => {
      // reset props to default
      this.resetChannelProps()
      this.resetLocalProps()

      // set template props
      for (const [key, value] of Object.entries(data)) {
        if (key === 'payload_add' && value === null) {
          continue
        }

        this.value[key] = value
      }
      if (
        this.value.push_action &&
        this.value.push_action.hasOwnProperty('action')
      ) {
        this.defaultAction = this.value.push_action.action
      }
      if (this.value.push_category_id) {
        this.fetchCategory(this.value.push_category_id)
        this.isPayloadBtnDisabled()
      }
    })

    if (this.value.push_category_id) {
      this.fetchCategory(this.value.push_category_id)
    }
    if (
      this.value.push_action &&
      this.value.push_action.hasOwnProperty('action')
    ) {
      this.defaultAction = this.value.push_action.action
    }
  },
  computed: {
    hasInApp() {
      return this.osType === 'ios'
        ? this.model.inapp_ios
        : this.model.inapp_android
    },
    isOptimove() {
      return this.model.trigger === campaignTriggerTypes.TRIGGER_OPTIMOVE
    },
    isSalesforce() {
      return this.model.trigger === campaignTriggerTypes.TRIGGER_SALESFORCE
    },
    appName() {
      return this.$store.state.app.project.name
    },

    appIcon() {
      return this.$store.state.app.project.icon || '@/img/icon/app-ico.png'
    },

    key() {
      return `${this.$route.query.channel}_${this.$route.query.variant}_${this.$route.query.lang}`
    },
    lang() {
      if (this.$route.query.lang === 'default') {
        return 'en'
      }
      return this.$route.query.lang
    }
  },
  methods: {
    resetChannelProps() {
      this.value.push_category_id = null
      this.value.push_title = null
      this.value.push_text = null
      this.value.push_picture = null
      this.value.push_action = {
        action: ''
      }
      this.value.push_buttons = {}
      this.value.payload_add = []
    },

    resetLocalProps() {
      this.category = ''
      this.defaultAction = ''
      this.isCategorySelected = false
      this.actions = []
      this.buttons = []
      this.pushCategoryPayload = []
    },

    querySearchCategory(queryString, cb) {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: 'push-categories',
          params: {
            title: queryString,
            type: this.osType
          }
        })
      ).then(({ data }) => {
        const list = data.map(item => {
          return {
            id: item.id,
            value: item.title
          }
        })
        cb(list)
      })
    },

    updateDefaultAction(value) {
      if (value === 'url') {
        delete this.value.push_action.deeplink
        this.$set(this.value.push_action, 'url', '')
      }

      if (value === 'deeplink') {
        delete this.value.push_action.url
        this.$set(this.value.push_action, 'deeplink', '')
      }

      if (value === '') {
        delete this.value.push_action.url
        delete this.value.push_action.deeplink
      }

      this.value.push_action.action = value

      if (value === 'inapp') {
        delete this.value.push_action.url
        delete this.value.push_action.deeplink
      }
    },

    handleSelectCategory(category) {
      this.value.push_category_id = category.id
      this.fetchCategory(category.id)
      if (
        Array.isArray(this.value.push_buttons) &&
        this.value.push_buttons.length === 0
      ) {
        this.value.push_buttons = {}
      }
    },

    clearCategorySelection() {
      const indicesToRemove = []
      if (this.value.payload_add) {
        this.value.payload_add.forEach((payload, index) => {
          if (payload.fromCategory) {
            indicesToRemove.push(index)
          }
        })
      }
      for (let i = indicesToRemove.length - 1; i >= 0; i--) {
        this.value.payload_add.splice(indicesToRemove[i], 1)
      }
      this.pushCategoryPayload = []
      this.actions = []
      this.buttons = []
      this.value.push_buttons = {}
      this.value.push_category_id = null
      this.defaultAction = ''
      this.isCategorySelected = false
    },

    fetchCategory(categoryId) {
      RestApi(
        projectEndpoint({
          method: 'get',
          url: 'push-categories',
          params: {
            id: categoryId
          }
        })
      ).then(({ data }) => {
        this.category = data[0].title
        this.actions = []
        this.pushCategoryPayload = []
        this.buttons = []

        const buttons = data[0].buttons || []
        const pushPayload = data[0].payloads || []

        buttons.forEach(button => {
          if (button.hasOwnProperty('title_languages')) {
            this.actions.push(button.title_languages[this.lang])
          } else {
            this.actions.push(button.title)
          }

          if (
            this.value.push_buttons &&
            !this.value.push_buttons.hasOwnProperty(button.id)
          ) {
            this.$set(this.value.push_buttons, button.id, {
              [button.action]: ''
            })
          }
        })
        this.$emit('set-actions', this.actions)

        this.defaultAction = data[0].action
        this.value.push_action.action = data[0].action
        if (data[0].action === 'inapp') {
          delete this.value.push_action.url
          delete this.value.push_action.deeplink
        }
        this.isCategorySelected = true

        this.buttons = buttons

        pushPayload.forEach(payload => {
          if (!payload.value && !this.doesPayloadExist(payload.key)) {
            payload.fromCategory = true
            if (this.value.payload_add === null) {
              this.value.payload_add = []
            }
            this.value.payload_add.push(payload)
          }
          if (payload.value) {
            this.pushCategoryPayload.push(payload)
          }
        })
      })
    },

    doesPayloadExist($key) {
      return (
        (this.pushCategoryPayload &&
          this.pushCategoryPayload.some(item => item.key === $key)) ||
        (this.value.payload_add &&
          this.value.payload_add.some(item => item.key === $key))
      )
    },

    addPayload() {
      if (this.value.payload_add === null) {
        this.value.payload_add = []
      }
      this.value.payload_add.push({ key: '', value: '' })
    },
    isPayloadBtnDisabled() {
      if (this.value.payload_add === null) {
        return false
      }
      if (
        !this.value.payload_add[this.value.payload_add.length - 1] ||
        this.value.payload_add[this.value.payload_add.length - 1].fromCategory
      ) {
        return false
      }
      return (
        this.value.payload_add[this.value.payload_add.length - 1].key === '' ||
        this.value.payload_add[this.value.payload_add.length - 1].value === ''
      )
    },
    removePayload(index) {
      if (
        this.value.payload_add[index].key === '' &&
        this.value.payload_add[index].value === ''
      ) {
        this.value.payload_add.splice(index, 1)

        return
      }

      this.$msgbox({
        title: this.$t('Common_Popup_Delete_Title'),
        message: this.$t('Common_Popup_Delete_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('Common_Popup_Delete_Confirm'),
        cancelButtonText: this.$t('Common_Popup_Delete_Cancel'),
        center: true
      })
        .then(() => {
          this.value.payload_add.splice(index, 1)
        })
        .catch(() => {})
    },

    removePicture() {
      this.$msgbox({
        title: this.$t('Common_Popup_Delete_Title'),
        message: this.$t('Common_Popup_Delete_Message'),
        showCancelButton: true,
        confirmButtonText: this.$t('Common_Popup_Delete_Confirm'),
        cancelButtonText: this.$t('Common_Popup_Delete_Cancel'),
        center: true
      })
        .then(() => {
          this.value.push_picture = null
        })
        .catch(() => {})
    }
  },
  watch: {
    'value.push_category_id': {
      handler(val) {
        if (val) {
          this.fetchCategory(val)
        }
      }
    }
  }
}
</script>

<style scoped>
.rowCt {
  display: flex;
  padding-bottom: 20px;
}

.rowCt div:not(:last-child) {
  padding-right: 10px;
}

.rowCt .icon {
  display: flex;
  justify-content: center;
  width: 25px;
  height: 40px;
  color: #a7acba;
}

.rowCt .icon:hover {
  cursor: pointer;
}

>>> .el-form-item {
  margin-bottom: 22px !important;
}

.push-icon-ct >>> .el-form-item__content {
  display: flex;
}

.push-icon-ct .icon-ct {
  align-self: center;
  margin-left: 10px;
}

.push-icon-ct .icon-ct .icon {
  font-size: 18px;
  color: #a6a6a6;
}

.push-icon-ct .icon-ct .icon:hover {
  cursor: pointer;
}

.preview {
  background-color: #f8fafc;
  border-radius: 4px;
  min-height: 610px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 44px 44px 0 44px !important;
  position: relative;
}
</style>



// WEBPACK FOOTER //
// src/modules/campaign-manage/components/content/channels/MobilePushForm.vue